import { AppMessageConditionType } from '@pushly/models/lib/enums/app-messages/app-message-condition-type'
import { AppMessagePriority } from '@pushly/models/lib/enums/app-messages/app-message-priority'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { AnimationType } from '@pushly/models/lib/enums/app-messages/app-message-animation-type'
import { AppMessageConditions } from '@pushly/models/lib/structs/app-messages/app-message-conditions'
import { AppMessageBehavior } from '@pushly/models/lib/structs/app-messages/app-message-behavior'
import { RelativeDate } from '@pushly/models/lib/structs/relative-date'
import { convertSeconds } from '../../_utils/time'

const PRIORITY_LABELS: Record<AppMessagePriority, string> = {
    [AppMessagePriority.HIGH]: 'High',
    [AppMessagePriority.DEFAULT]: 'Default',
    [AppMessagePriority.LOW]: 'Low',
}

/**
 * The animation names a sentence needs, which differ from the dropdown labels:
 * a summary reads "slides from top in", where the control reads "Slide from Top".
 */
const ENTER_ANIMATION_PHRASES: Record<AnimationType, string> = {
    [AnimationType.FADE]: 'fades in',
    [AnimationType.SLIDE_LEFT]: 'slides from right in',
    [AnimationType.SLIDE_RIGHT]: 'slides from left in',
    [AnimationType.SLIDE_DOWN]: 'slides from top in',
    [AnimationType.SLIDE_UP]: 'slides from bottom in',
    [AnimationType.ZOOM_IN]: 'zooms in',
    [AnimationType.ZOOM_OUT]: 'zooms out on entry',
}

const EXIT_ANIMATION_PHRASES: Record<AnimationType, string> = {
    [AnimationType.FADE]: 'fades out',
    [AnimationType.SLIDE_LEFT]: 'slides to left out',
    [AnimationType.SLIDE_RIGHT]: 'slides to right out',
    [AnimationType.SLIDE_DOWN]: 'slides to bottom out',
    [AnimationType.SLIDE_UP]: 'slides to top out',
    [AnimationType.ZOOM_IN]: 'zooms in on exit',
    [AnimationType.ZOOM_OUT]: 'zooms out',
}

/** Renders a duration for prose: "1 minute", "2 minutes", "30 seconds". */
function describeDuration(duration: RelativeDate | null | undefined): string | undefined {
    if (!duration?.intervalSeconds) {
        return undefined
    }

    const metric = duration.displayMetric ?? RelativeDateDisplayMetric.SECONDS
    const exact = convertSeconds(duration.intervalSeconds, metric)
    if (!exact) {
        return undefined
    }

    // Rounded to one place, because a stored value need not divide evenly into
    // the metric it is displayed in. A message saved before the seeding fix
    // carries one second while reading in days, which printed as "0.0000115
    // days" — legible as a defect, but the same arithmetic turns any legacy value
    // into a wall of decimals. An approximation reads as an approximation.
    const amount = Math.round(exact * 10) / 10
    if (!amount) {
        // Rounded away to nothing: smaller than the unit can express, so the
        // number is not worth printing.
        return `less than 1 ${metric.replace(/s$/, '')}`
    }

    // Every metric in this enum is a plain plural formed by a trailing 's'.
    const unit = amount === 1 ? metric.replace(/s$/, '') : metric

    return `${amount} ${unit}`
}

/**
 * Every animation type is paired with FADE for a smooth finish, so the named
 * animation is whichever one is not FADE — falling back to FADE when it is the
 * only entry.
 */
function describeAnimation(animations: readonly AnimationType[] | undefined, phrases: Record<AnimationType, string>) {
    if (!animations?.length) {
        return undefined
    }

    const named = animations.length > 1 ? animations.find((a) => a !== AnimationType.FADE) : animations[0]

    return named ? phrases[named] : undefined
}

/**
 * A one-line description of when this message shows, covering every trigger
 * type the builder supports.
 */
export function getTriggerSummary(conditions: AppMessageConditions | null | undefined): string {
    switch (conditions?.type) {
        case AppMessageConditionType.SESSION_START:
            return 'Displays when a new session begins.'

        case AppMessageConditionType.SESSION_TIME_IN_APP: {
            const duration = describeDuration(conditions?.timeInApp)
            return duration
                ? `Displays after ${duration} in the current session.`
                : 'Displays after a period of time in the current session.'
        }

        case AppMessageConditionType.LIFETIME_TIME_IN_APP: {
            const duration = describeDuration(conditions?.timeInApp)
            return duration
                ? `Displays after ${duration} in the app in total.`
                : 'Displays after a period of time in the app in total.'
        }

        case AppMessageConditionType.CUSTOM: {
            const key = conditions?.triggers?.key?.trim()
            const value = conditions?.triggers?.value?.toString().trim()

            return key && value ? `Displays when ${key} reaches ${value}.` : 'Custom trigger not configured yet.'
        }

        default:
            return 'Displays immediately, or the next time the app opens.'
    }
}

/**
 * A one-line description of how this message behaves once it is on screen,
 * composed from the four things a reader would ask about: how it ranks against
 * other messages, how it goes away, whether it comes back, and how it moves.
 */
export function getOnScreenSummary(behavior: AppMessageBehavior, priority: AppMessagePriority): string {
    const clauses: string[] = [`${PRIORITY_LABELS[priority] ?? PRIORITY_LABELS[AppMessagePriority.DEFAULT]} priority`]

    const autoDismiss = describeDuration(behavior.maxDisplayTime)
    clauses.push(autoDismiss ? `dismisses itself after ${autoDismiss}` : 'stays until dismissed')

    const redisplayInterval = behavior.redisplay?.enabled ? describeDuration(behavior.redisplay.fcap) : undefined
    clauses.push(redisplayInterval ? `may show again after ${redisplayInterval}` : 'shown once')

    const enter = behavior.displayAnimations?.enter
    const exit = behavior.displayAnimations?.exit
    const enterPhrase = enter?.enabled ? describeAnimation(enter.animations, ENTER_ANIMATION_PHRASES) : undefined
    const exitPhrase = exit?.enabled ? describeAnimation(exit.animations, EXIT_ANIMATION_PHRASES) : undefined

    if (enterPhrase === 'fades in' && exitPhrase === 'fades out') {
        clauses.push('fades in and out')
    } else if (enterPhrase && exitPhrase) {
        clauses.push(`${enterPhrase} and ${exitPhrase}`)
    } else if (enterPhrase || exitPhrase) {
        clauses.push((enterPhrase ?? exitPhrase)!)
    } else {
        clauses.push('no animation')
    }

    return clauses.join(' · ')
}
